import { useMemo, type MouseEvent, type ReactNode } from "react"
import type { ColumnSizingState } from "@tanstack/react-table"
import DropdownMenu from "@/v2-ui/dropdownMenu"
import Separator from "@/v2-ui/separator"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useTableContext } from "./table.context"
import {
  getColumnTitle,
  canColumnGroup,
  measureAllNaturalColumnWidths,
} from "./table.utils"
import { useTableExport } from "./export/table.export"
import { useTableSummary } from "./summary/table.summary.context"
import type { ColumnFilterConfig } from "./table.types"

const NO_GROUPING = "__none__"

export type TableToolbarMenuProps = {
  onRefresh?(): void
  /** When provided, shows an "Export to Excel" button. The value is used as the file name (without extension). */
  exportFileName?: string
  addTimestampToFileName?: boolean
  children?: ReactNode
}

function TableToolbarMenu(props: TableToolbarMenuProps) {
  const {
    onRefresh,
    exportFileName,
    addTimestampToFileName = true,
    children,
  } = props

  const {
    table,
    activePanel,
    setActivePanel,
    hasPersistence,
    resetTableState,
    resetQueryState,
    advancedFilter,
  } = useTableContext()

  const tableExport = useTableExport({
    fileName: exportFileName,
    addTimestampToFileName,
  })
  const { isVisible: isSummaryVisible, setIsVisible: setIsSummaryVisible } =
    useTableSummary()

  const filterableColumns = useMemo(() => {
    return table.getAllColumns().filter((column) => {
      if (column.id === "__select") return false
      const meta = column.columnDef.meta as
        | { filter?: ColumnFilterConfig }
        | undefined
      return !!meta?.filter && !meta.filter.toolbar
    })
  }, [table])

  const hidableColumns = useMemo(() => {
    return table.getAllColumns().filter((c) => {
      if (c.id === "__select") return false
      return c.getCanHide()
    })
  }, [table])

  const groupableColumns = useMemo(
    () => table.getAllColumns().filter(canColumnGroup),
    [table],
  )

  function autoFitAllColumns(e: MouseEvent<HTMLButtonElement>) {
    const container = (e.currentTarget as HTMLElement).closest(
      ".ui-table-container",
    )
    const tableEl = container?.querySelector("table") as HTMLTableElement | null
    if (!tableEl) return

    const headers = table.getLeafHeaders()
    const targetIndices: number[] = []
    const targetHeaders: typeof headers = []

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      if (!header.column.getCanResize()) continue
      if (header.column.id.startsWith("__")) continue
      targetIndices.push(i)
      targetHeaders.push(header)
    }

    const widths = measureAllNaturalColumnWidths(tableEl, targetIndices)
    const sizing: ColumnSizingState = {}

    for (let i = 0; i < targetIndices.length; i++) {
      const width = widths.get(targetIndices[i]) ?? 0
      if (width > 0) {
        const minSize = targetHeaders[i].column.columnDef.minSize ?? 40
        sizing[targetHeaders[i].column.id] = Math.max(width, minSize) + 1
      }
    }

    if (Object.keys(sizing).length > 0) {
      table.setColumnSizing((prev) => ({ ...prev, ...sizing }))
    }
  }

  const { columnFilters, grouping, sorting, globalFilter, pagination } =
    table.getState()
  const panelColumnIds = new Set(filterableColumns.map((c) => c.id))
  const activeFilterCount = columnFilters.filter((f) =>
    panelColumnIds.has(f.id),
  ).length
  const currentGrouping = grouping.length > 0 ? grouping[0] : NO_GROUPING

  // The "reset" button only appears when there's something to reset: any
  // column filter, the search box, an advanced filter, active grouping, a
  // sort, or a non-first page. Column layout (sizing/order/visibility) stays
  // out — that has its own "Reset table layout" action.
  const hasQueryState =
    columnFilters.length > 0 ||
    sorting.length > 0 ||
    grouping.length > 0 ||
    !!globalFilter ||
    !!advancedFilter ||
    (pagination?.pageIndex ?? 0) > 0

  return (
    <>
      {children}
      {children && <Separator orientation="vertical" />}

      {onRefresh && (
        <TooltipButton
          variant="ghost"
          size="xs"
          isSquare
          iconSrc="refresh"
          content="Refresh"
          onClick={() => {
            onRefresh?.()
          }}
        />
      )}

      {exportFileName && (
        <TooltipButton
          variant="ghost"
          size="xs"
          isSquare
          iconSrc={tableExport.isExporting ? "spinner" : "file-export"}
          content="Export to Excel"
          disabled={tableExport.isExporting}
          onClick={() => {
            tableExport.exportToExcel()
          }}
        />
      )}

      {filterableColumns.length > 0 && (
        <TooltipButton
          variant={
            activePanel === "filter" || activePanel === "advancedFilter"
              ? "secondary"
              : "ghost"
          }
          size="xs"
          isSquare
          iconSrc="filter"
          content={
            activeFilterCount > 0
              ? `Filter (${activeFilterCount} active)`
              : "Filter"
          }
          onClick={() => {
            const isOpen =
              activePanel === "filter" || activePanel === "advancedFilter"
            setActivePanel(isOpen ? null : "filter")
          }}
        />
      )}

      {hasQueryState && (
        <TooltipButton
          variant="ghost"
          size="xs"
          isSquare
          iconSrc="filter-slash"
          content="Reset filters, grouping, sorting & pagination"
          onClick={() => {
            resetQueryState()
          }}
        />
      )}

      {hidableColumns.length > 0 && (
        <TooltipButton
          variant={activePanel === "columns" ? "secondary" : "ghost"}
          size="xs"
          isSquare
          iconSrc="columns-3"
          content="Columns"
          onClick={() => {
            setActivePanel(activePanel === "columns" ? null : "columns")
          }}
        />
      )}

      <Separator orientation="vertical" />

      {groupableColumns.length > 0 && (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <TooltipButton
              variant="ghost"
              size="xs"
              isSquare
              iconSrc="apps-sort"
              content="Group by"
            />
          </DropdownMenu.Trigger>

          <DropdownMenu.Content
            align="start"
            className="ui-table-group-selector-content"
          >
            <DropdownMenu.RadioGroup
              value={currentGrouping}
              onValueChange={(columnId) => {
                table.setGrouping(columnId === NO_GROUPING ? [] : [columnId])
              }}
            >
              <DropdownMenu.RadioItem value={NO_GROUPING}>
                No grouping
              </DropdownMenu.RadioItem>
              <DropdownMenu.Separator />
              {groupableColumns.map((column) => (
                <DropdownMenu.RadioItem key={column.id} value={column.id}>
                  {getColumnTitle(column)}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu>
      )}

      <TooltipButton
        variant={isSummaryVisible ? "secondary" : "ghost"}
        size="xs"
        isSquare
        iconSrc="sigma"
        content={isSummaryVisible ? "Hide summary row" : "Summary row"}
        onClick={() => {
          setIsSummaryVisible(!isSummaryVisible)
        }}
      />

      {hasPersistence && (
        <TooltipButton
          variant="ghost"
          size="xs"
          isSquare
          iconSrc="text-width"
          content="Auto-fit columns"
          onClick={autoFitAllColumns}
        />
      )}

      {hasPersistence && (
        <TooltipButton
          variant="ghost"
          size="xs"
          isSquare
          iconSrc="rotate-left"
          content="Reset table layout"
          onClick={() => {
            resetTableState()
          }}
        />
      )}
    </>
  )
}

export default TableToolbarMenu
